import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { loadTrust, TrustDocument } from "../services/platformSource";
import "../styles/revamp.css";

/**
 * Security & Trust.
 *
 * Content comes from the CMS (Admin → CMS → Security Page) and the public route
 * returns nothing until an administrator explicitly publishes it. Until then this
 * renders a short holding state and sets noindex, so a half-written security
 * posture cannot be crawled, shared, or relied on by a buyer.
 *
 * The guard is mechanical rather than a note someone might miss, and there is no
 * static fallback on purpose: every line on this page is a representation about
 * how customer data is handled, and inventing one is the single thing this page
 * must never do.
 */

const Security: React.FC = () => {
    // null until the CMS answers, and null for good if the page is unpublished.
    // There is deliberately no static fallback: inventing security claims is the
    // one thing this page must never do.
    const [doc, setDoc] = useState<TrustDocument | null>(null);

    useEffect(() => {
        let cancelled = false;
        loadTrust().then((next) => {
            if (!cancelled) setDoc(next);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    if (!doc) {
        return (
            <>
                <Seo
                    title="Security"
                    description="How MindSyncX handles customer data."
                    path="/security"
                    noIndex
                />
                <main className="mx-detail">
                    <div className="container">
                        <p className="mx-eyebrow">Security</p>
                        <h1 className="mx-detail-title">Security details on request.</h1>
                        <p className="mx-detail-prose">
                            We are preparing a full write-up of how MindSyncX handles customer
                            data — encryption, tenant isolation, where models run, retention and
                            subprocessors. Until it is published we would rather answer directly
                            than approximate.
                        </p>
                        <p className="mx-detail-prose">
                            Ask us and you will get a specific answer, not a brochure.
                        </p>
                        <div className="mx-suite-actions" style={{ marginTop: 28 }}>
                            <Link className="mx-btn mx-btn-primary" to="/contact">
                                Ask a security question
                            </Link>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    return (
        <>
            <Seo
                title="Security"
                description="How MindSyncX encrypts, isolates and retains customer data, where models run, and who we share data with."
                path="/security"
            />
            <main className="mx-detail">
                <div className="container">
                    <p className="mx-eyebrow">Security &amp; trust</p>
                    <h1 className="mx-detail-title">How your data is handled.</h1>
                    <p className="mx-detail-tagline">
                        Specifics, not assurances. If something here is not precise enough for
                        your review, ask and we will answer it directly.
                    </p>

                    {doc.certifications.length > 0 && (
                        <ul className="mx-detail-metrics">
                            {doc.certifications.map((c) => (
                                <li key={c.name}>
                                    <span className="mx-metric-value">{c.name}</span>
                                    <span className="mx-metric-label">{c.detail}</span>
                                </li>
                            ))}
                        </ul>
                    )}

                    {doc.sections.map((section) => (
                        <section className="mx-detail-section" key={section.key} id={section.key}>
                            <p className="mx-eyebrow">{section.title}</p>
                            <p className="mx-detail-prose">{section.body}</p>
                            <ul className="mx-capabilities" style={{ marginTop: 18 }}>
                                {section.points.map((point) => (
                                    <li key={point}>
                                        <span className="mx-dot" />
                                        {point}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ))}

                    <section className="mx-detail-cta">
                        <h2 className="mx-card-title">Report a vulnerability</h2>
                        <p className="mx-card-body" style={{ flex: "0 0 auto" }}>
                            {doc.securityContact || "Contact us and we will route it to the right engineer."}
                        </p>
                    </section>
                </div>
            </main>
        </>
    );
};

export default Security;
